import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {
  BulkAssemblyConstructsStructuredTableMetadata,
  BulkAssemblyConstructsStructuredTableRowMetadata,
} from 'molbio/bulk-assembly/tables/BulkAssemblyConstructsStructuredTableAdapter';
import {
  BulkAssemblyFragmentsStructuredTableMetadata,
  BulkAssemblyFragmentsStructuredTableRowMetadata,
} from 'molbio/bulk-assembly/tables/BulkAssemblyFragmentsStructuredTableAdapter';
import ActionFunctionButton from 'notebook/structured-tables/action-functions/ActionFunctionButton';
import makeCustomButtonActionFunction from 'notebook/structured-tables/action-functions/makeCustomButtonActionFunction';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import {
  AxisContextMenuActionFunctionParameters,
  StructuredTableContextMenuItem,
} from 'notebook/structured-tables/StructuredTableConstants';
import {
  TableActionFunctionParameters,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {MultiSelectionBox, SelectionRange} from 'notebook/tables/constants/TableSelectionConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';

function getViewableSelectedRows<
  TBulkAssemblyStructuredTableRowMetadata,
  TViewableBulkAssemblyStructuredTableRowMetadata extends TBulkAssemblyStructuredTableRowMetadata,
>(
  selectionRange: SelectionRange | null,
  rows: Array<StructuredTableRow<TBulkAssemblyStructuredTableRowMetadata>>,
  isRowViewable: (
    row: StructuredTableRow<TBulkAssemblyStructuredTableRowMetadata>
  ) => row is StructuredTableRow<TViewableBulkAssemblyStructuredTableRowMetadata>
): Array<StructuredTableRow<TViewableBulkAssemblyStructuredTableRowMetadata>> | null {
  if (selectionRange == null) {
    return null;
  }
  const selectedRows = rows.slice(selectionRange.minIndex, selectionRange.maxIndex + 1);
  if (selectedRows.every((row) => isRowViewable(row))) {
    return selectedRows;
  }
  return null;
}

type ViewableBulkAssemblyConstructsStructuredTableRowMetadata =
  BulkAssemblyConstructsStructuredTableRowMetadata & {constructCid: string; canPreview: true};

function getViewableSelectedConstructRows(
  selectionRange: SelectionRange | null,
  rows: Array<StructuredTableRow<BulkAssemblyConstructsStructuredTableRowMetadata>>
): Array<StructuredTableRow<ViewableBulkAssemblyConstructsStructuredTableRowMetadata>> | null {
  return getViewableSelectedRows<
    BulkAssemblyConstructsStructuredTableRowMetadata,
    ViewableBulkAssemblyConstructsStructuredTableRowMetadata
  >(
    selectionRange,
    rows,
    (
      row: StructuredTableRow<BulkAssemblyConstructsStructuredTableRowMetadata>
    ): row is StructuredTableRow<ViewableBulkAssemblyConstructsStructuredTableRowMetadata> =>
      row.metadata.constructCid != null && row.metadata.canPreview
  );
}

type ViewableBulkAssemblyFragmentsStructuredTableRowMetadata =
  BulkAssemblyFragmentsStructuredTableRowMetadata & {fragmentCid: string};

function getViewableSelectedFragmentRows(
  selectionRange: SelectionRange | null,
  rows: Array<StructuredTableRow<BulkAssemblyFragmentsStructuredTableRowMetadata>>
): Array<StructuredTableRow<ViewableBulkAssemblyFragmentsStructuredTableRowMetadata>> | null {
  return getViewableSelectedRows<
    BulkAssemblyFragmentsStructuredTableRowMetadata,
    ViewableBulkAssemblyFragmentsStructuredTableRowMetadata
  >(
    selectionRange,
    rows,
    (
      row: StructuredTableRow<BulkAssemblyFragmentsStructuredTableRowMetadata>
    ): row is StructuredTableRow<ViewableBulkAssemblyFragmentsStructuredTableRowMetadata> =>
      row.metadata.fragmentCid != null && row.cells[0].typedValue?.type === BaseTypedValueType.BIOENTITY
  );
}

function getViewFragmentsActionTitle(
  tableMetadata: BulkAssemblyFragmentsStructuredTableMetadata,
  count: number
): string {
  const actionVerb = tableMetadata.isReadOnly ? 'View' : 'Edit';
  return `${actionVerb} ${pluralize(count, 'fragment')}`;
}

function getViewConstructsActionFunction({
  selectionRange,
  structuredTable,
}: AxisContextMenuActionFunctionParameters<
  BulkAssemblyConstructsStructuredTableMetadata,
  BulkAssemblyConstructsStructuredTableRowMetadata
>): StructuredTableContextMenuItem | null {
  const validSelectedrows = getViewableSelectedConstructRows(selectionRange, structuredTable.rows);
  if (validSelectedrows) {
    return {
      name: pluralize(validSelectedrows.length, 'View construct'),
      onSelect: () => {
        structuredTable.metadata.onViewConstructs(
          validSelectedrows.map(({metadata}) => metadata.constructCid)
        );
      },
    };
  } else {
    return null;
  }
}

function getViewFragmentsActionFunction({
  selectionRange,
  structuredTable,
}: AxisContextMenuActionFunctionParameters<
  BulkAssemblyFragmentsStructuredTableMetadata,
  BulkAssemblyFragmentsStructuredTableRowMetadata
>): StructuredTableContextMenuItem | null {
  const validSelectedRows = getViewableSelectedFragmentRows(selectionRange, structuredTable.rows);
  if (validSelectedRows) {
    return {
      name: getViewFragmentsActionTitle(structuredTable.metadata, validSelectedRows.length),
      onSelect: () => {
        structuredTable.metadata.onViewFragments(validSelectedRows.map(({metadata}) => metadata.fragmentCid));
      },
    };
  } else {
    return null;
  }
}

function selectionRangeFromMultiSelectionBox(
  multiSelectionBox: MultiSelectionBox | null
): SelectionRange | null {
  if (multiSelectionBox == null) {
    return null;
  }
  return {minIndex: multiSelectionBox.minI, maxIndex: multiSelectionBox.maxI};
}

function renderViewConstructsActionFunction({
  structuredTable,
  multiSelectionBox,
}: TableActionFunctionParameters<
  BulkAssemblyConstructsStructuredTableMetadata,
  BulkAssemblyConstructsStructuredTableRowMetadata
>): JSX.Element {
  const selectionRange = selectionRangeFromMultiSelectionBox(multiSelectionBox);
  const validSelectedRows = getViewableSelectedConstructRows(selectionRange, structuredTable.rows);
  return (
    <ActionFunctionButton
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      disabled={validSelectedRows == null}
      buttonText={pluralize(validSelectedRows?.length ?? 2, 'View construct')}
      onClick={() => {
        structuredTable.metadata.onViewConstructs(
          validSelectedRows!.map(({metadata}) => metadata.constructCid)
        );
      }}
      tooltip=""
    />
  );
}

function renderViewFragmentsActionFunction({
  structuredTable,
  multiSelectionBox,
}: TableActionFunctionParameters<
  BulkAssemblyFragmentsStructuredTableMetadata,
  BulkAssemblyFragmentsStructuredTableRowMetadata
>): JSX.Element {
  const selectionRange = selectionRangeFromMultiSelectionBox(multiSelectionBox);
  const validSelectedRows = getViewableSelectedFragmentRows(selectionRange, structuredTable.rows);
  return (
    <ActionFunctionButton
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      disabled={validSelectedRows == null}
      buttonText={getViewFragmentsActionTitle(structuredTable.metadata, validSelectedRows?.length ?? 2)}
      onClick={() => {
        structuredTable.metadata.onViewFragments(
          validSelectedRows!.map(({metadata}) => metadata.fragmentCid)
        );
      }}
      tooltip=""
    />
  );
}

export const viewConstructsAxisContextMenuActionFunction = {
  getRowAxisContextMenuItem: getViewConstructsActionFunction,
};
export const viewFragmentsAxisContextMenuActionFunction = {
  getRowAxisContextMenuItem: getViewFragmentsActionFunction,
};

export const viewConstructsActionFunction = makeCustomButtonActionFunction<
  BulkAssemblyConstructsStructuredTableMetadata,
  BulkAssemblyConstructsStructuredTableRowMetadata
>({
  getHidden: () => false,
  renderToolbarButton: renderViewConstructsActionFunction,
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
});

export const viewFragmentsActionFunction = makeCustomButtonActionFunction<
  BulkAssemblyFragmentsStructuredTableMetadata,
  BulkAssemblyFragmentsStructuredTableRowMetadata
>({
  getHidden: () => false,
  renderToolbarButton: renderViewFragmentsActionFunction,
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
});
